import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Spinner,
  Form,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
  Row,
  Col,
  FormFeedback,
  Progress,
} from "reactstrap";
import {
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { userCreate, roles, userUpdate } from "../../redux/actions/user";
import { useLocation } from "react-router-dom";
import Loading from "../../components/Loading/Loading";

function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}

function getStrengthColor(score) {
  if (score <= 1) return "danger";
  if (score <= 2) return "warning";
  if (score <= 3) return "info";
  return "success";
}

function getStrengthLabel(score) {
  if (score <= 1) return "Muy débil";
  if (score <= 2) return "Débil";
  if (score <= 3) return "Moderada";
  if (score <= 4) return "Fuerte";
  return "Muy fuerte";
}

function validatePassword(password) {
  const errors = [];
  if (password.length < 8) errors.push("Mínimo 8 caracteres");
  if (!/[A-Z]/.test(password)) errors.push("Al menos una mayúscula");
  if (!/[0-9]/.test(password)) errors.push("Al menos un número");
  return errors;
}

function UserCreate() {
  const dispatch = useDispatch();
  const currentLang = useSelector((state) => state.lang.lang);
  const response = useSelector((state) => state.user);
  const getRoles = useSelector((state) => state.user.roles);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("vendedor");
  const [phone, setPhone] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [update, setUpdate] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [dropdownOpen, setOpen] = useState(false);
  const location = useLocation();

  const toggle = () => setOpen(!dropdownOpen);

  const passwordErrors = password ? validatePassword(password) : [];
  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword;
  const isPasswordValid =
    passwordErrors.length === 0 && (update || passwordsMatch);

  useEffect(() => {

    if (response.status === "success") {
      setAlertTitle(response.message);
      setAlertMessage(response.message);
      setLoading(false);
      setShowAlert(true);
      setEmail("");
      setName("");
      setPhone("");
      setRole("");
      setPassword("");
      setConfirmPassword("");
    }
  }, [response]);

  useEffect(() => {
    document.title = "BGL - Crear Usuario";
    if (!getRoles) {
      dispatch(roles());
    }
    setLoading(false);
  }, [getRoles]);

  useEffect(() => {
    if (!location.state?.data) {
      return;
    } else if (update === false) {
      let data = location.state.data;
      setEmail(data.email);
      setName(data.name);
      setPhone(data.phone);
      setRole(data.role_name);
      setUpdate(true);
    }
    setLoading(false);
  }, [location.state]);

  const onDismiss = () => setShowAlert(false);

  const onFormSubmit = (e) => {
    e.preventDefault();

    // Validate password for new users (required) or if provided for updates
    if (!update && !password) {
      setAlertTitle("Error");
      setAlertMessage("La contraseña es requerida");
      setShowAlert(true);
      return;
    }

    if (password && passwordErrors.length > 0) {
      setAlertTitle("Error");
      setAlertMessage(passwordErrors.join(", "));
      setShowAlert(true);
      return;
    }

    if (password && !passwordsMatch) {
      setAlertTitle("Error");
      setAlertMessage("Las contraseñas no coinciden");
      setShowAlert(true);
      return;
    }

    setLoading(true);
    if (update) {
      dispatch(userUpdate(email, password, role, name, phone));
    } else {
      dispatch(userCreate(email, password, role, name, phone));
    }
  };

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert isOpen={showAlert} toggle={onDismiss}>
          {alertTitle} - {alertMessage}
        </Alert>
      </Container>
      <Container className="container-border p-3 mt-3">
        <Row>
          <Col lg="4"></Col>
          <Col sm="12" lg="12">
            <Form onSubmit={(e) => onFormSubmit(e)}>
              <FormGroup>
                <Label style={{ fontSize: "24px" }} for="role">
                  Tipo de Usuario
                </Label>

                <ButtonDropdown
                  className="ml-3"
                  isOpen={dropdownOpen}
                  toggle={toggle}
                  disabled={loading}
                >
                  <DropdownToggle caret>{role}</DropdownToggle>
                  <DropdownMenu className="container-border">
                    {getRoles &&
                      getRoles.map((rol, index) =>
                        rol.role_name !== role ? (
                          <DropdownItem
                            key={index}
                            onClick={(e) => setRole(e.target?.value)}
                            value={rol.role_name}
                          >
                            {rol.role_name}
                          </DropdownItem>
                        ) : (
                          <DropdownItem
                            key={index}
                            onClick={(e) => setRole(e.target?.value)}
                            selected
                            value={rol.role_name}
                          >
                            {rol.role_name}
                          </DropdownItem>
                        )
                      )}
                  </DropdownMenu>
                </ButtonDropdown>
              </FormGroup>
              <FormGroup>
                <Label for="email">Email</Label>
                <Input
                  type="text"
                  placeholder="Email..."
                  disabled={update || loading}
                  onChange={(e) => setEmail(e.target?.value)}
                  value={email}
                />
              </FormGroup>
              <FormGroup>
                <Label for="password">
                  Password {update && "(dejar vacío para mantener)"}
                </Label>
                <Input
                  type="password"
                  placeholder="· · · · · ·"
                  disabled={loading}
                  onChange={(e) => setPassword(e.target?.value)}
                  value={password}
                  invalid={password.length > 0 && passwordErrors.length > 0}
                  valid={password.length > 0 && passwordErrors.length === 0}
                />
                {password.length > 0 && passwordErrors.length > 0 && (
                  <FormFeedback>{passwordErrors.join(", ")}</FormFeedback>
                )}
                {password.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Fortaleza: {getStrengthLabel(passwordStrength)}
                    </small>
                    <Progress
                      value={(passwordStrength / 5) * 100}
                      color={getStrengthColor(passwordStrength)}
                      className="mt-1"
                      style={{ height: "6px" }}
                    />
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="confirmPassword">Confirmar Password</Label>
                <Input
                  type="password"
                  placeholder="· · · · · ·"
                  disabled={loading}
                  onChange={(e) => setConfirmPassword(e.target?.value)}
                  value={confirmPassword}
                  invalid={
                    confirmPassword.length > 0 && !passwordsMatch
                  }
                  valid={confirmPassword.length > 0 && passwordsMatch}
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <FormFeedback>Las contraseñas no coinciden</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="name">Nombre</Label>
                <Input
                  type="text"
                  placeholder="Nombre..."
                  disabled={loading}
                  onChange={(e) => setName(e.target?.value)}
                  value={name}
                />
              </FormGroup>
              <FormGroup>
                <Label for="phone">Telefono</Label>
                <Input
                  type="text"
                  placeholder="Telefono..."
                  disabled={loading}
                  onChange={(e) => setPhone(e.target?.value)}
                  value={phone}
                />
              </FormGroup>
              <Button
                color="success"
                disabled={loading || (password && !isPasswordValid)}
              >
                Guardar
              </Button>
            </Form>
          </Col>
          <Col lg="4"></Col>
        </Row>
      </Container>
    </>
  );
}
export default UserCreate;
